import { getDialCode } from '@ab-platform/ui';
import {
  type PhoneEntry,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';
import { composeCanonicalRui } from '@/lib/ruiInput';
import type {
  AddressPayload,
  ContactChannelPayload,
  SubmitOnboardingPayload,
} from '@/lib/api';

/**
 * Build the BE-shaped payload from the RHF form state. Pure function so
 * the SummaryPage submit handler stays focused on routing + error display.
 *
 * Conversions:
 *   - empty strings → undefined (drops the optional field)
 *   - YYYY-MM-DD dates → ISO 8601 with T00:00:00Z
 *   - province / country uppercased
 *   - email + CF trimmed (no normalisation beyond that — BE owns canonical)
 *   - agreementType derived from orgType via the closed lookup below
 *
 * PR-A.1 invariants — these fields MUST be present and non-empty on the
 * payload, otherwise BE returns 400 VALIDATION_FAILED:
 *   adminUser.sex                    closed enum, no normalization here
 *   adminUser.dateOfBirth            ISO 8601 datetime
 *   adminUser.birthCountry           ISO 3166-1 alpha-2 uppercase
 *   adminUser.birthMunicipalityCadastralCode  required iff birthCountry === 'IT'
 *
 * PR-C additions:
 *   - `addresses[]` derived from the multi-slot form state. Empty +
 *     disabled slots are dropped. addressType set per legalNature.
 *   - `contactChannels[]` from `orgContacts`. EMAIL + PEC always present;
 *     MOBILE + PHONE only when the user typed a value. The email channel
 *     is marked `isPrimary` (single anchor for the BE channel index).
 *   - `headquartersAddress` keeps its legacy slot — derived from the
 *     primary address slot so the BE wire shape doesn't change shape.
 */
const AGREEMENT_TYPE_BY_ORG_TYPE = {
  BROKER: 'B_WITH_B',
  AGENZIA: 'B_WITH_A',
  INTERMEDIARIO: 'B_WITH_E',
} as const;

export function buildPayload(
  values: RegistrationFormValues,
): SubmitOnboardingPayload {
  const empty = (s: string | undefined): string | undefined =>
    s && s.trim().length > 0 ? s.trim() : undefined;
  const dateToIso = (s: string): string =>
    `${s.trim()}T00:00:00.000Z`;

  const isPerson = values.organization.legalNature === 'PERSON';

  // ── Addresses ─────────────────────────────────────────────────────────
  // Primary slot first (REGISTERED_OFFICE for PG, RESIDENCE for PF) and
  // marked `isPrimary` so the BE assigns it to `organizations.headquarters_
  // address` in the post-insert mirror.
  const addresses: AddressPayload[] = [];
  addresses.push({
    addressType: isPerson ? 'RESIDENCE' : 'REGISTERED_OFFICE',
    ...normalizeAddress(values.addresses.primary),
    isPrimary: true,
  });
  if (!isPerson) {
    if (values.addresses.alternateOffice.enabled) {
      addresses.push({
        addressType: 'OPERATIVE_OFFICE',
        ...normalizeAddress(values.addresses.alternateOffice.data),
      });
    }
    if (values.addresses.mailingAddress.enabled) {
      addresses.push({
        addressType: 'MAILING',
        ...normalizeAddress(values.addresses.mailingAddress.data),
      });
    }
  } else {
    if (values.addresses.alternateResidence.enabled) {
      addresses.push({
        addressType: 'DOMICILE',
        ...normalizeAddress(values.addresses.alternateResidence.data),
      });
    }
  }

  // ── Contact channels ──────────────────────────────────────────────────
  // EMAIL + PEC always emitted (schema enforces non-empty).
  // PHONE (fisso): emitted from `orgContacts.phone` when the local number
  //   is non-empty. Required for COMPANY (cross-field), optional for PERSON.
  // MOBILE: emitted when ANY of the two mobile slots is filled —
  //   - PERSON  → `orgContacts.mobile` (the org cellulare = the person)
  //   - COMPANY → `adminUser.mobilePhone` (the signatory's cellulare)
  //               + optionally `orgContacts.mobile` if the user filled the
  //                 separate org-level field
  // EMAIL is the primary anchor; the first MOBILE entry is also primary
  // so the BE channel index has a consistent reference.
  const contactChannels: ContactChannelPayload[] = [];
  contactChannels.push({
    type: 'EMAIL',
    value: values.orgContacts.email.trim(),
    isPrimary: true,
  });
  contactChannels.push({
    type: 'PEC',
    value: values.orgContacts.pec.trim(),
  });

  const phoneStr = composePhone(values.orgContacts.phone);
  if (phoneStr) {
    contactChannels.push({ type: 'PHONE', value: phoneStr });
  }

  const orgMobileStr = composePhone(values.orgContacts.mobile);
  const adminMobileStr = composePhone(values.adminUser.mobilePhone);
  // Pick the "primary" mobile per legalNature. For PG the signatory's
  // mobile carries more weight (used to reach the user that will own the
  // area-riservata access). For PF the two coincide; we still emit both
  // if the user filled them separately.
  const primaryMobile = isPerson ? orgMobileStr : adminMobileStr;
  const secondaryMobile = isPerson ? adminMobileStr : orgMobileStr;
  if (primaryMobile) {
    contactChannels.push({
      type: 'MOBILE',
      value: primaryMobile,
      isPrimary: true,
    });
  }
  if (secondaryMobile && secondaryMobile !== primaryMobile) {
    contactChannels.push({ type: 'MOBILE', value: secondaryMobile });
  }

  return {
    organization: {
      legalNature: values.organization.legalNature,
      legalName: values.organization.legalName.trim(),
      orgType: values.organization.orgType,
      fiscalCode: empty(values.organization.fiscalCode),
      vatNumber: empty(values.organization.vatNumber),
      // Compose the BE-canonical RUI (section letter + 9 digits) from the
      // derived section — the form holds only the digits (UX-INPUT-1).
      ruiNumber: empty(
        composeCanonicalRui(
          values.organization.orgType,
          values.organization.ruiNumber,
        ),
      ),
      ruiRegistrationDate: emptyDateToIso(values.organization.ruiRegistrationDate),
      // Codice SDI — emitted only for COMPANY. PERSON branch keeps it
      // unset (not applicable). Pre-filled by the company-lookup
      // endpoint when the provider returns it (PR-OpenAPI-2); also
      // editable manually.
      sdiCode: isPerson ? undefined : empty(values.organization.sdiCode),
      // Tipo impresa — emitted only for COMPANY (irrelevant for PERSON;
      // the BE rejects it on PERSON anyway).
      businessLegalForm: isPerson
        ? undefined
        : empty(values.organization.businessLegalForm),
      withholdingType:
        values.organization.withholdingType === ''
          ? undefined
          : values.organization.withholdingType,
      headquartersAddress: {
        ...normalizeAddress(values.addresses.primary),
      },
      addresses,
      contactChannels,
    },
    adminUser: {
      fullName: values.adminUser.fullName.trim(),
      email: values.adminUser.email.trim(),
      fiscalCode: values.adminUser.fiscalCode.trim().toUpperCase(),
      sex: values.adminUser.sex,
      dateOfBirth: dateToIso(values.adminUser.dateOfBirth),
      birthCountry: values.adminUser.birthCountry.trim().toUpperCase(),
      birthMunicipalityCadastralCode: empty(
        values.adminUser.birthMunicipalityCadastralCode,
      ),
      placeOfBirth: empty(values.adminUser.placeOfBirth),
      // Personal RUI of the responsible — PR-ONBOARDING-COMPANY-RESPONSIBLE-RUI-1.
      // Emitted when present (required for AGENZIA/BROKER COMPANY); the section
      // is derived from org_type and prepended to the canonical (UX-INPUT-1).
      ruiNumber: empty(
        composeCanonicalRui(
          values.organization.orgType,
          values.adminUser.ruiNumber,
        ),
      ),
      ruiRegistrationDate: emptyDateToIso(values.adminUser.ruiRegistrationDate),
    },
    agreement: {
      agreementType: AGREEMENT_TYPE_BY_ORG_TYPE[values.organization.orgType],
    },
    turnstileToken: values.turnstileToken,
  };
}

function normalizeAddress(entry: {
  street: string;
  city: string;
  province: string;
  zip: string;
  country: string;
}): {
  street: string;
  city: string;
  province: string;
  zip: string;
  country: string;
} {
  return {
    street: entry.street.trim(),
    city: entry.city.trim(),
    province: entry.province.trim().toUpperCase(),
    zip: entry.zip.trim(),
    country: entry.country.trim().toUpperCase(),
  };
}

function emptyDateToIso(s: string | undefined): string | undefined {
  const t = s && s.trim().length > 0 ? s.trim() : undefined;
  if (!t) return undefined;
  return `${t}T00:00:00.000Z`;
}

/**
 * Compose a `{ prefixIso, number }` form value into an E.164-ish wire
 * string (e.g. "+39 348 1234567"). Returns undefined when the local
 * number is empty so the caller can drop the channel cleanly.
 *
 * Spaces between the prefix and the local part are preserved as-typed
 * by the user (we don't normalise digits) — the BE just stores the
 * string. Future tightening to E.164 (digits-only, no spaces, country
 * dial agreed up front) belongs to a verification PR, not here.
 */
function composePhone(entry: PhoneEntry): string | undefined {
  const local = entry.number.trim();
  if (local.length === 0) return undefined;
  const dial = getDialCode(entry.prefixIso) ?? '';
  const prefix = dial.length > 0 ? `+${dial} ` : '';
  return `${prefix}${local}`;
}
